import React, { useEffect, useState } from 'react';
import { MicIcon, StopCircleIcon, PlayIcon, PauseIcon, TrashIcon, CheckIcon, ClipboardIcon } from 'lucide-react';
import { useRecording } from '../../contexts/RecordingContext';
import { useClosingAssistant } from '../../contexts/ClosingAssistantContext';
interface AudioTabProps {
  onTranscriptReady?: () => void;
}
export const AudioTab: React.FC<AudioTabProps> = ({
  onTranscriptReady
}) => {
  const {
    recordingState,
    startRecording,
    stopRecording,
    transcript,
    recordings
  } = useRecording();
  const {
    updateReadinessScore,
    addInsight
  } = useClosingAssistant();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [keywordHighlights, setKeywordHighlights] = useState<{
    word: string;
    type: string;
  }[]>([]);
  // Analyze transcript for keywords when it changes
  useEffect(() => {
    if (transcript) {
      setIsAnalyzing(true);
      // Simulate keyword analysis
      setTimeout(() => {
        const keywords = analyzeTranscript(transcript);
        setKeywordHighlights(keywords);
        setIsAnalyzing(false);
        // Update readiness score based on positive keywords
        const positiveKeywords = keywords.filter(k => k.type === 'positive').length;
        const negativeKeywords = keywords.filter(k => k.type === 'negative').length;
        if (positiveKeywords > negativeKeywords) {
          updateReadinessScore(prev => Math.min(100, prev + 10));
          // Add insight about positive customer sentiment
          addInsight({
            type: 'behavior',
            message: 'Positive sentiment detected in conversation',
            severity: 'success',
            category: 'conversation'
          });
        } else if (negativeKeywords > 0) {
          // Add insight about objections
          addInsight({
            type: 'objection',
            message: 'Potential objection detected in conversation',
            severity: 'warning',
            category: 'conversation'
          });
        }
        // Notify parent that transcript is ready
        if (onTranscriptReady) {
          onTranscriptReady();
        }
      }, 1500);
    }
  }, [transcript]);
  // Mock function to analyze transcript for keywords
  const analyzeTranscript = (text: string): {
    word: string;
    type: string;
  }[] => {
    const positiveWords = ['interested', 'like', 'good', 'great', 'yes', 'perfect', 'when', 'book', 'reserve'];
    const negativeWords = ['expensive', 'unsure', 'think', 'maybe', 'later', 'concern', 'problem', 'issue'];
    const productWords = ['price', 'tour', 'hotel', 'flight', 'vacation', 'package', 'dates', 'availability'];
    const keywords: {
      word: string;
      type: string;
    }[] = [];
    const words = text.toLowerCase().split(/\s+/);
    words.forEach(word => {
      if (positiveWords.includes(word)) {
        keywords.push({
          word,
          type: 'positive'
        });
      } else if (negativeWords.includes(word)) {
        keywords.push({
          word,
          type: 'negative'
        });
      } else if (productWords.includes(word)) {
        keywords.push({
          word,
          type: 'product'
        });
      }
    });
    return keywords;
  };
  // Highlight keywords in transcript
  const highlightTranscript = (text: string) => {
    if (!text || keywordHighlights.length === 0) return text;
    let highlightedText = text;
    keywordHighlights.forEach(({
      word,
      type
    }) => {
      const regex = new RegExp(`\\b${word}\\b`, 'gi');
      const className = type === 'positive' ? 'text-green-600 font-medium' : type === 'negative' ? 'text-red-600 font-medium' : 'text-blue-600 font-medium';
      highlightedText = highlightedText.replace(regex, `<span class="${className}">$&</span>`);
    });
    return highlightedText;
  };
  const handleCopyTranscript = () => {
    if (transcript) {
      navigator.clipboard.writeText(transcript).then(() => {
        alert('Transcript copied to clipboard!');
      }).catch(err => {
        console.error('Failed to copy transcript: ', err);
      });
    }
  };
  return <div className="p-4 h-full flex flex-col" data-id="element-124">
      <div className="mb-4 text-center" data-id="element-125">
        <h3 className="text-lg font-medium text-gray-800 mb-1" data-id="element-126">
          Voice Assistant
        </h3>
        <p className="text-sm text-gray-500" data-id="element-127">
          Record and analyze customer conversations
        </p>
      </div>
      {/* Recording controls */}
      <div className="flex justify-center mb-6" data-id="element-128">
        {recordingState === 'inactive' ? <button onClick={startRecording} className="flex items-center justify-center h-16 w-16 bg-red-600 text-white rounded-full hover:bg-red-700" data-id="element-129">
            <MicIcon className="h-8 w-8" data-id="element-130" />
          </button> : <button onClick={stopRecording} className="flex items-center justify-center h-16 w-16 bg-gray-600 text-white rounded-full hover:bg-gray-700 animate-pulse" data-id="element-131">
            <StopCircleIcon className="h-8 w-8" data-id="element-132" />
          </button>}
      </div>
      {recordingState === 'recording' && <div className="text-center mb-4" data-id="element-133">
          <div className="inline-flex items-center px-3 py-1 bg-red-100 text-red-800 rounded-full" data-id="element-134">
            <span className="h-2 w-2 bg-red-600 rounded-full mr-2 animate-pulse" data-id="element-135"></span>
            <span className="text-sm font-medium" data-id="element-136">Recording...</span>
          </div>
          <p className="text-xs text-gray-500 mt-1" data-id="element-137">
            Speak clearly for best results
          </p>
        </div>}
      {/* Transcript display */}
      {transcript && <div className="flex-1 overflow-y-auto" data-id="element-138">
          <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-white" data-id="element-139">
            <div className="flex items-center justify-between mb-2" data-id="element-140">
              <h4 className="text-sm font-medium text-gray-700" data-id="element-141">
                Latest Transcript
              </h4>
              <div className="flex space-x-1" data-id="element-142">
                <button onClick={handleCopyTranscript} className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded" data-id="element-143">
                  <ClipboardIcon className="h-4 w-4" data-id="element-144" />
                </button>
              </div>
            </div>
            {isAnalyzing ? <div className="flex items-center justify-center p-4" data-id="element-145">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500 mr-2" data-id="element-146"></div>
                <span className="text-sm text-gray-600" data-id="element-147">
                  Analyzing conversation...
                </span>
              </div> : <div className="text-sm text-gray-700" data-id="element-148">
                <div dangerouslySetInnerHTML={{
            __html: highlightTranscript(transcript)
          }} data-id="element-149" />
              </div>}
          </div>
          {/* Keyword analysis */}
          {keywordHighlights.length > 0 && <div className="border border-gray-200 rounded-lg p-4 bg-white mb-4" data-id="element-150">
              <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-151">
                Conversation Analysis
              </h4>
              <div className="space-y-2" data-id="element-152">
                <div className="flex flex-wrap gap-2" data-id="element-153">
                  {keywordHighlights.filter(k => k.type === 'positive').map((keyword, index) => <span key={index} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full" data-id="element-154">
                        {keyword.word}
                      </span>)}
                </div>
                <div className="flex flex-wrap gap-2" data-id="element-155">
                  {keywordHighlights.filter(k => k.type === 'negative').map((keyword, index) => <span key={index} className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full" data-id="element-156">
                        {keyword.word}
                      </span>)}
                </div>
                <div className="flex flex-wrap gap-2" data-id="element-157">
                  {keywordHighlights.filter(k => k.type === 'product').map((keyword, index) => <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full" data-id="element-158">
                        {keyword.word}
                      </span>)}
                </div>
              </div>
            </div>}
        </div>}
      {/* Recent recordings list */}
      {recordings.length > 0 && <div className="mt-auto" data-id="element-159">
          <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-160">
            Recent Recordings
          </h4>
          <div className="space-y-2 max-h-40 overflow-y-auto" data-id="element-161">
            {recordings.slice(0, 3).map((recording, index) => <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg" data-id="element-162">
                <div className="flex items-center" data-id="element-163">
                  <PlayIcon className="h-4 w-4 text-gray-500 mr-2" data-id="element-164" />
                  <div data-id="element-165">
                    <p className="text-xs font-medium text-gray-700" data-id="element-166">
                      Recording {recordings.length - index}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-167">
                      {new Date(recording.timestamp).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                    </p>
                  </div>
                </div>
                <span className="text-xs text-gray-500" data-id="element-168">
                  {recording.duration}s
                </span>
              </div>)}
          </div>
        </div>}
    </div>;
};